const SCRIPT_ID = 'signature-cryptopro-api';
const LOAD_TIMEOUT_MS = 20000;
const GOST_ALGORITHMS = ['1.2.643.2.2.19', '1.2.643.7.1.1.1.1', '1.2.643.7.1.1.1.2'];

let loadingPlugin;

export class SignatureClientError extends Error {
  constructor(message) {
    super(message);
    this.signatureClientError = true;
  }
}

export const assertActive = (isActive) => {
  if (!isActive()) throw new SignatureClientError('Операция отменена. Повторите вход через УКЭП.');
};

const loadScript = () => new Promise((resolve, reject) => {
  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.src = `${process.env.BASE_URL}vendor/cryptopro/cadesplugin_api.js`;
  script.async = true;
  const timer = setTimeout(() => {
    script.remove();
    reject(new SignatureClientError('Не удалось загрузить КриптоПро. Обновите страницу и попробуйте ещё раз.'));
  }, LOAD_TIMEOUT_MS);
  script.onload = () => { clearTimeout(timer); resolve(); };
  script.onerror = () => {
    clearTimeout(timer);
    script.remove();
    reject(new SignatureClientError('Не удалось загрузить КриптоПро. Проверьте соединение.'));
  };
  document.head.appendChild(script);
});

export const getCryptoPro = async () => {
  if (!loadingPlugin) {
    loadingPlugin = (async () => {
      if (!window.cadesplugin) await loadScript();
      try {
        await window.cadesplugin;
      } catch (error) {
        throw new SignatureClientError('КриптоПро недоступен. Установите CSP, Browser plug-in и включите расширение браузера.');
      }
      const cp = window.cadesplugin;
      if (!cp?.CreateObjectAsync) {
        throw new SignatureClientError('Этот браузер не поддерживает асинхронный API КриптоПро. Используйте поддерживаемый браузер.');
      }
      // The plugin itself is a thenable; return a wrapper to preserve its API.
      return { cp };
    })().catch((error) => {
      loadingPlugin = undefined;
      throw error;
    });
  }
  return loadingPlugin;
};

const withStore = async (callback) => {
  const { cp } = await getCryptoPro();
  const store = await cp.CreateObjectAsync('CAdESCOM.Store');
  await store.Open(cp.CAPICOM_CURRENT_USER_STORE, cp.CAPICOM_MY_STORE, cp.CAPICOM_STORE_OPEN_MAXIMUM_ALLOWED);
  try {
    return await callback(cp, await store.Certificates);
  } finally {
    await store.Close();
  }
};

const usableCertificate = async (certificate) => {
  const validFrom = new Date(await certificate.ValidFromDate);
  const validTo = new Date(await certificate.ValidToDate);
  if (!(validFrom <= new Date() && validTo > new Date()) || !await certificate.HasPrivateKey()) return false;
  const publicKey = await certificate.PublicKey();
  const algorithm = await publicKey.Algorithm;
  return GOST_ALGORITHMS.includes(await algorithm.Value);
};

export const listSigningCertificates = () => withStore(async (cp, certificates) => {
  const result = [];
  const count = await certificates.Count;
  // Native certificate objects are read sequentially while this store is open.
  /* eslint-disable no-await-in-loop */
  for (let index = 1; index <= count; index++) {
    const certificate = await certificates.Item(index);
    if (await usableCertificate(certificate)) {
      result.push({
        thumbprint: await certificate.Thumbprint,
        name: await certificate.GetInfo(cp.CAPICOM_CERT_INFO_SUBJECT_SIMPLE_NAME),
        issuer: await certificate.GetInfo(cp.CAPICOM_CERT_INFO_ISSUER_SIMPLE_NAME),
        expiresAt: new Date(await certificate.ValidToDate).toISOString(),
      });
    }
  }
  /* eslint-enable no-await-in-loop */
  return result;
});

export const signChallenge = (challenge, thumbprint, isActive = () => true) => withStore(async (cp, certificates) => {
  assertActive(isActive);
  const matches = await certificates.Find(cp.CAPICOM_CERTIFICATE_FIND_SHA1_HASH, thumbprint);
  if (!await matches.Count) throw new SignatureClientError('Выбранный сертификат не найден. Подключите токен и обновите список.');
  const certificate = await matches.Item(1);
  if (!await usableCertificate(certificate)) throw new SignatureClientError('Сертификат недоступен для подписания или его срок действия истёк.');
  const signer = await cp.CreateObjectAsync('CAdESCOM.CPSigner');
  await signer.propset_Certificate(certificate);
  await signer.propset_CheckCertificate(true);
  await signer.propset_Options(cp.CAPICOM_CERTIFICATE_INCLUDE_END_ENTITY_ONLY);
  const signedData = await cp.CreateObjectAsync('CAdESCOM.CadesSignedData');
  await signedData.propset_ContentEncoding(cp.CADESCOM_BASE64_TO_BINARY);
  await signedData.propset_Content(challenge);
  assertActive(isActive);
  const signature = await signedData.SignCades(signer, cp.CADESCOM_CADES_BES, true);
  assertActive(isActive);
  return signature;
});

export const signingErrorMessage = (error) => {
  if (error?.signatureClientError) return error.message;
  return 'Не удалось создать подпись. Проверьте токен, сертификат и PIN, затем повторите попытку.';
};
